import { useCallback } from "react";
import { useSelector } from "react-redux";
import fr from "@/locales/fr.json";
import en from "@/locales/en.json";
import ar from "@/locales/ar.json";

const LOCALES = { fr, en, ar };

/**
 * i18n hook. Supports dot-notation keys and {{variable}} interpolation.
 *
 * With no argument, reads the dashboard user's UI language from Redux
 * (`state.language.current`) — this is what most call sites should use.
 * Pass an explicit locale to override it (used by the public survey-taking
 * flow, which follows the *survey's own authored language* rather than the
 * viewer's UI language preference).
 *
 * @param {string} [explicitLocale] - locale key to force, bypassing Redux
 * @returns {{ t: (key: string, vars?: Record<string, string | number>) => string, locale: string }}
 *
 * @example
 * const { t } = useTranslation();
 * t("actions.next")                           // → "Suivant"
 * t("survey.question_of", { current: 1, total: 5 }) // → "Question 1 sur 5"
 */
export function useTranslation(explicitLocale) {
    const current = useSelector((s) => s.language?.current) ?? "en";
    const locale = explicitLocale ?? current;
    const messages = LOCALES[locale] ?? LOCALES.en;

    const t = useCallback(
        (key, vars = {}) => {
            // Resolve dot-notation key
            const value = key.split(".").reduce((obj, k) => obj?.[k], messages);

            if (typeof value !== "string") {
                // Fallback: return the key itself so missing strings are visible
                console.warn(`[i18n] Missing translation key: "${key}"`);
                return key;
            }

            // Replace {{variable}} placeholders
            return value.replace(/\{\{(\w+)\}\}/g, (_, name) =>
                vars[name] !== undefined ? String(vars[name]) : `{{${name}}}`
            );
        },
        [messages]
    );

    return { t, locale };
}